import { TICKET_PRIORITY_VALUES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, ErrorState, Field, Icon, Input, Select, Skeleton, Textarea, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { RequesterPicker } from "../../service/tickets/new-ticket-contact";
import { TypeFields } from "../../service/tickets/new-ticket-fields";
import { priorityOf, validateDraft, type FieldValue, type NewTicketDraft, type Problems } from "../../service/tickets/new-ticket-form";
import { NEW_TICKET_DESCRIPTION_MAX, NEW_TICKET_TITLE_MAX } from "../../service/tickets/ticket-constants";
import type { Option } from "../../service/tickets/ticket-filters-popover";
import type { TicketTypeOut } from "../../service/tickets/ticket-types";
import { useCreateTicket, useTypeFields } from "../../service/tickets/use-new-ticket";
import { raisableTypes, useTicketOrganisations, useTicketTypes } from "../../service/tickets/use-ticket-options";
import { convertBody, convertDraft, defaultType, handoffSummary } from "./convert-ticket-model";
import type { useConversationActions } from "./inbox-api";
import type { ConversationDetail } from "./inbox-model";

const FORM_ID = "convert-ticket-form";

interface DialogProps {
  detail: ConversationDetail;
  actions: ReturnType<typeof useConversationActions>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const typeLabel = (type: TicketTypeOut, language: string): string => (language === "fil" ? type.name.fil : undefined) ?? type.name.en;

function FormSkeleton() {
  return (
    <div data-testid="convert-skeleton" aria-hidden="true" className="space-y-4">
      <Skeleton className="h-16 w-full" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

interface FormProps extends Omit<DialogProps, "open"> {
  types: readonly TicketTypeOut[];
  create: ReturnType<typeof useCreateTicket>;
}

function ConvertForm({ detail, actions, types, create, onOpenChange }: FormProps) {
  const { t, i18n } = useTranslation();
  const errorMessage = useErrorMessage();
  const organisations = useTicketOrganisations();
  const summary = handoffSummary(detail);
  const [draft, setDraft] = useState<NewTicketDraft>(() => convertDraft(defaultType(types), detail));
  const [keepOpen, setKeepOpen] = useState(true);
  const [problems, setProblems] = useState<Problems>({});
  const [failure, setFailure] = useState<string | null>(null);
  const typeFields = useTypeFields(draft.typeId);
  const patch = (next: Partial<NewTicketDraft>) => {
    setDraft((current) => ({ ...current, ...next }));
    setFailure(null);
  };
  const orgOptions: Option[] | null = organisations.available ? organisations.options.map((option) => ({ value: option.id, label: option.name })) : null;

  const settle = async (number: string) => {
    if (keepOpen) {
      toast.success(t("service.inbox.convert.created", { number }));
      return;
    }
    try {
      await actions.setStatus.mutateAsync("resolved");
      toast.success(t("service.inbox.convert.createdResolved", { number }));
    } catch {
      toast.warning(t("service.inbox.convert.resolveFailed", { number }));
    }
  };

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const found = validateDraft(draft, typeFields.fields);
    setProblems(found);
    if (Object.keys(found).length > 0) return;
    create.mutate(convertBody(draft, typeFields.fields, detail.conversation.id), {
      onSuccess: (ticket) => {
        onOpenChange(false);
        void settle(ticket.number);
      },
      onError: (error) => {
        setFailure(errorMessage(error));
      },
    });
  };

  return (
    <form id={FORM_ID} noValidate onSubmit={submit} className="space-y-4">
      {failure ? (
        <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm font-semibold text-danger">
          {failure}
        </p>
      ) : null}
      <div className="space-y-2">
        <RequesterPicker draft={draft} problems={problems} organisations={orgOptions} onChange={patch} />
        {draft.requesterMode === "new" ? <p className="text-xs text-fg-subtle">{t("service.inbox.convert.requesterFromChat", { name: detail.visitor.display_name })}</p> : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("service.tickets.new.type")} error={problems.type ? t(problems.type.key) : undefined}>
          <Select
            value={draft.typeId}
            onChange={(event) => {
              patch({ typeId: event.target.value, priority: priorityOf(types.find((type) => type.id === event.target.value)), fields: {} });
            }}
          >
            {types.map((type) => (
              <option key={type.id} value={type.id}>
                {typeLabel(type, i18n.language)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("service.tickets.new.priority")}>
          <Select value={draft.priority} onChange={(event) => { patch({ priority: TICKET_PRIORITY_VALUES.find((value) => value === event.target.value) ?? "normal" }); }}>
            {[...TICKET_PRIORITY_VALUES].reverse().map((value) => (
              <option key={value} value={value}>
                {t(`service.chips.priority.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Field label={t("service.tickets.new.title")} hint={t("service.tickets.new.titleHint")} error={problems.title ? t(problems.title.key, problems.title.values) : undefined}>
        <Input value={draft.title} maxLength={NEW_TICKET_TITLE_MAX} autoComplete="off" required onChange={(event) => { patch({ title: event.target.value }); }} />
      </Field>
      <Field label={summary ? t("service.inbox.convert.descriptionLabel") : t("service.tickets.new.description")} hint={summary ? t("service.inbox.convert.descriptionHint") : t("service.inbox.convert.summaryNone")}>
        <Textarea rows={4} maxLength={NEW_TICKET_DESCRIPTION_MAX} value={draft.description} onChange={(event) => { patch({ description: event.target.value }); }} />
      </Field>
      {typeFields.isPending ? (
        <FormSkeleton />
      ) : (
        <TypeFields
          fields={typeFields.fields}
          values={draft.fields}
          problems={problems}
          language={i18n.language}
          onChange={(key: string, value: FieldValue) => {
            setDraft((current) => ({ ...current, fields: { ...current.fields, [key]: value } }));
          }}
        />
      )}
      <Checkbox checked={draft.notify} onCheckedChange={(checked) => { patch({ notify: checked === true }); }} label={t("service.inbox.convert.notify")} description={t("service.inbox.convert.notifyHint")} />
      <Checkbox checked={keepOpen} onCheckedChange={(checked) => { setKeepOpen(checked === true); }} label={t("service.inbox.convert.keepOpen")} description={t("service.inbox.convert.keepOpenHint")} />
    </form>
  );
}

function ConvertBody({ detail, actions, onOpenChange }: Omit<DialogProps, "open">) {
  const { t } = useTranslation();
  const types = useTicketTypes();
  const create = useCreateTicket();
  const raisable = raisableTypes(types.data);
  const draftName = detail.visitor.display_name;
  const ready = !types.isPending && !types.isError && raisable.length > 0;
  return (
    <DialogContent
      className="max-w-2xl"
      title={t("service.inbox.convert.title")}
      description={t("service.inbox.convert.description", { name: draftName })}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
            {t("service.inbox.convert.cancel")}
          </Button>
          <Button type="submit" form={FORM_ID} loading={create.isPending} disabled={!ready}>
            <Icon name="ticket" />
            {t("service.inbox.convert.submit")}
          </Button>
        </>
      }
    >
      {types.isPending ? <FormSkeleton /> : null}
      {types.isError ? <ErrorState headingLevel={3} title={t("service.inbox.convert.typesError")} retryLabel={t("service.error.retry")} onRetry={() => void types.refetch()} /> : null}
      {ready ? <ConvertForm detail={detail} actions={actions} types={raisable} create={create} onOpenChange={onOpenChange} /> : null}
      {!types.isPending && !types.isError && raisable.length === 0 ? <p className="text-sm text-fg-muted">{t("service.inbox.convert.noTypes")}</p> : null}
    </DialogContent>
  );
}

/**
 * "Convert to ticket" (CH-015): raises a ticket from the open chat. The form starts from the handoff summary, the chat's title and the
 * visitor as the requester; the ticket is linked to the chat, and the chat either stays with the team or is resolved (the toggle).
 */
export function ConvertTicketDialog({ detail, actions, open, onOpenChange }: DialogProps) {
  return <Dialog open={open} onOpenChange={onOpenChange}>{open ? <ConvertBody detail={detail} actions={actions} onOpenChange={onOpenChange} /> : null}</Dialog>;
}
